import { DateTime, DurationLike } from "luxon";
import { DOB_FORMAT } from "../types/customerDetail";

export type DateObject = {
  day: string | number;
  month: string | number;
  year: string | number;
};

/**
 * This should ONLY be used when rendering a plain date! Resulting DateTime is
 * not the same absolute point in time!
 * This function takes just the date out of the iso string. Formatting this date
 * will always result in the date shown in the string, regardless of offset or
 * lack thereof
 * @param isoDate
 * @param dateOnlyFormat
 */
export const getDateFormatted = (
  isoDate: DateObject | string | null | undefined,
  dateOnlyFormat: string
): string => {
  if (!isoDate) {
    return "";
  }
  return getUTCDate(isoDate).toFormat(dateOnlyFormat);
};

/**
 * Get a UTC DateTime in the future or past based on the given Duration offset
 * i.e. Passing `{ days: 3 }` returns non formatted date 3 days in the future
 */
export const getDateOffset = (duration: DurationLike) => {
  return DateTime.utc().plus(duration);
};

/**
 * Get a UTC DateTime in the future or past based on the given Duration offset
 * i.e. Passing `{ days: 3 }` returns a formatted date 3 days in the future
 */
export const getFormattedDateOffset = (duration: DurationLike) => {
  return getDateOffset(duration).toFormat(DOB_FORMAT);
};

/**
 * Takes an ISO date string or DateObject and returns a Luxon DateTime in UTC,
 * with non-date portions forcibly ignored. This is used for capturing and sending
 * ONLY a date (e.g. in add/edit cohort) where we only want the date, not time.
 * @param isoDate
 */
export const getUTCDate = (isoDate: DateObject | string | null | undefined): DateTime => {
  if (!isoDate) {
    return DateTime.utc().startOf("day");
  }
  if (typeof isoDate === "string") {
    return DateTime.fromISO(isoDate, {
      zone: "utc",
      setZone: true,
    }).startOf("day");
  } else {
    return DateTime.fromObject(
      {
        day: Number(isoDate.day),
        month: Number(isoDate.month),
        year: Number(isoDate.year),
      },
      {
        zone: "utc",
      }
    );
  }
};

/**
 * This helper formats a date time in the time zone specified in the date
 * string, and optionally converts it to the timeZone passed in.
 * For example, this is intended to be used for displaying date times in the
 * Provider's given view. The date time will be displayed as though you are in
 * whatever given offset the iso string currently has set, or as the timeZone
 * passed.
 * @param isoDate
 * @param format
 * @param timeZone
 */
export const getDateTimeFormatted = (
  isoDate: string | null | undefined,
  format: string,
  timeZone?: string
): string => {
  if (!isoDate) {
    return "";
  }
  const fromISO = DateTime.fromISO(isoDate, {
    setZone: true,
  }).toUTC();
  return fromISO.setZone(timeZone ?? fromISO.zone).toFormat(format);
};

export const isSameYear = (
  isoDate: string | DateObject,
  comparisonISODate?: string | DateObject
) => {
  const dateFromISOOrObject = (isoOrObject: string | DateObject) =>
    typeof isoOrObject === "string"
      ? DateTime.fromISO(isoOrObject, {
          setZone: true,
        })
      : DateTime.fromObject({
          day: Number(isoOrObject.day),
          month: Number(isoOrObject.month),
          year: Number(isoOrObject.year),
        });
  const date = dateFromISOOrObject(isoDate);
  const comparisonDate = comparisonISODate
    ? dateFromISOOrObject(comparisonISODate)
    : DateTime.local().setZone(date.zone);
  // Not using hasSame here because it ignores timezones. We're dealing
  // with absolute points in time, so timezone is relevant
  // return date.toUTC().year === comparisonDate.toUTC().year;
  return date.hasSame(comparisonDate, "year");
};

export const hasSame = (
  firstISO: string,
  secondISO: string,
  unit: Parameters<DateTime["hasSame"]>[1]
) => DateTime.fromISO(firstISO).hasSame(DateTime.fromISO(secondISO), unit);

export const addDays = (date: string, days: number) => {
  const origDate = DateTime.fromISO(date).toUTC();

  if (!origDate.isValid) {
    return null;
  }

  return origDate.plus({ days }).toISO();
};
